import React, { useMemo, useEffect, useCallback, useId, useRef } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import { urlToList } from 'utils';
import useQueryString from 'hooks/use-query-string';
import styled from 'styled-components';
import BWButton from 'components/shared/BWButton';
import CheckAccess from 'navigation/CheckAccess';
import { PortalIcon } from 'utils/baochauTheme';

const ComponentRender = styled.div`
  display: ${(props) => (props.hidden ? 'none' : '')};
`;

const normalizePanelKey = (value) => String(value ?? '').toLowerCase();

const handleTabKeyDown = (event) => {
  if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

  const tabs = Array.from(event.currentTarget.closest('[role="tablist"]')?.querySelectorAll('[role="tab"]') || []);
  const currentIndex = tabs.indexOf(event.currentTarget);
  if (currentIndex < 0 || tabs.length === 0) return;

  event.preventDefault();
  const nextIndex =
    event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? tabs.length - 1
        : (currentIndex + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;

  tabs[nextIndex].focus();
  tabs[nextIndex].click();
};

const Panel = ({
  onSubmit,
  loading,
  panes,
  actions,
  noActions,
  purchaseOrderId,
  hasSubmit,
  closeLocation,
  renderInactivePanes = true,
  ...props
}) => {
  const methods = useFormContext();
  const tabListId = useId().replace(/:/g, '');
  const tabListRef = useRef(null);

  const [query, setQuery] = useQueryString();
  const tab_active = query?.tab_active ?? '';
  const defaultTabKey = panes[0]?.key?.toLowerCase();
  const { pathname } = useLocation();
  const params = useParams();

  const id = useMemo(() => {
    const getNameFunction = pathname.split('/')[1];
    return params[`${getNameFunction}_id`] ?? params?.id ?? params[Object.keys(params).find((p) => p.includes('id'))];
  }, [params, pathname]);

  const isView = useMemo(() => pathname.toLowerCase().includes('/detail') || pathname.includes('/view'), [pathname]);
  const isAdd = useMemo(() => pathname.toLowerCase().includes('/add'), [pathname]);
  const isEdit = useMemo(() => pathname.toLowerCase().includes('/edit'), [pathname]);
  const history = useHistory();
  const path = urlToList(useLocation().pathname)[0];

  useEffect(() => {
    if (
      pathname === '/stocks-in-request/add' &&
      isAdd &&
      purchaseOrderId &&
      tab_active === defaultTabKey
    ) {
      setQuery({ ...query, purchase_order_id: purchaseOrderId }, true);
    }
  }, [defaultTabKey, isAdd, pathname, purchaseOrderId, query, setQuery, tab_active]);

  useEffect(() => {
    if (!tab_active) {
      setQuery(
        {
          ...query,
          tab_active: defaultTabKey,
        },
        true, // replace route trước đó thành route có tab_active trong history
      );
    }
  }, [defaultTabKey, query, setQuery, tab_active]);

  const parseActionValue = useMemo(() => {
    return Array.isArray(actions) && actions?.filter((p) => !p.hidden);
  }, [actions]);
  const parsePanel = useMemo(() => panes?.filter((p) => !p.hidden) ?? [], [panes]);

  const getErrorFieldNames = useCallback((errors = {}, parentName = '') => {
    return Object.entries(errors).flatMap(([fieldName, value]) => {
      const fullFieldName = parentName ? `${parentName}.${fieldName}` : fieldName;
      if (!value || typeof value !== 'object') return [fullFieldName];
      if (value.message || value.type || value.ref) return [fullFieldName];
      return getErrorFieldNames(value, fullFieldName);
    });
  }, []);

  const handleInvalidSubmit = useCallback(
    (errors) => {
      const errorFieldNames = getErrorFieldNames(errors);
      const panelWithError = parsePanel.find((pane) =>
        pane?.fieldActive?.some((fieldName) =>
          errorFieldNames.some((errorName) => errorName === fieldName || errorName.startsWith(`${fieldName}.`)),
        ),
      );

      if (panelWithError?.key && panelWithError.key.toString().toLowerCase() !== tab_active?.toLowerCase()) {
        setQuery(
          {
            ...query,
            tab_active: panelWithError.key.toString().toLowerCase(),
          },
          true,
        );
      }

      if (errorFieldNames[0]) {
        setTimeout(() => methods.setFocus(errorFieldNames[0]), 0);
      }
    },
    [getErrorFieldNames, methods, parsePanel, query, setQuery, tab_active],
  );

  const goToEditPath = useCallback(
    (e) => {
      e.preventDefault();
      history.push(`${path}/edit/${id}`);
    },
    [history, id, path],
  );
  const goToPreviousPath = useCallback(() => {
    history.push(closeLocation || `${path}`);
  }, [closeLocation, history, path]);

  const jsx_footer = useMemo(() => {
    if (noActions) {
      return;
    }
    if (Boolean(parseActionValue.length)) {
      return parseActionValue.map((props, i) => <BWButton key={i} className='mr-2' {...props} />);
    } else {
      if (isView) {
        return (
          <button type='button' className='bw_btn_outline bw_btn_outline_success' onClick={goToEditPath}>
            Chỉnh sửa
          </button>
        );
      } else if (isAdd || isEdit) {
        return (
          <button type='submit' className='bw_btn bw_btn_success'>
            <PortalIcon icon='fi fi-rr-check' className='bw_btn_icon' />
            Hoàn tất {isEdit ? 'chỉnh sửa' : 'thêm mới'}
          </button>
        );
      }
    }
  }, [goToEditPath, isView, isEdit, isAdd, parseActionValue, noActions]);

  const jsx_close = useMemo(() => {
    if (!noActions) {
      return (
        <button type='button' className='bw_btn_outline' onClick={goToPreviousPath}>
          Đóng
        </button>
      );
    }
  }, [goToPreviousPath, noActions]);

  const jsx_submit = !noActions && (
    <div className='bw_btn_save bw_btn_group bw_flex bw_justify_content_right bw_align_items_center'>
      {hasSubmit && jsx_footer}
      {jsx_close}
    </div>
  );

  const jsx_main = (
    <React.Fragment>
      <ul ref={tabListRef} className='bw_tabs' role='tablist' aria-label='Các nhóm thông tin'>
        {parsePanel.map((e) => {
          const paneKey = normalizePanelKey(e?.key);
          const isActive = normalizePanelKey(tab_active) === paneKey;
          const safePaneKey = paneKey.replace(/[^a-z0-9_-]/g, '-');
          const tabId = `${tabListId}-tab-${safePaneKey}`;
          const panelId = `${tabListId}-panel-${safePaneKey}`;
          const tabButton = (
            <button
              id={tabId}
              type='button'
              role='tab'
              aria-selected={isActive}
              aria-controls={panelId}
              tabIndex={isActive ? 0 : -1}
              className='bw_link bw_tab_button'
              onKeyDown={handleTabKeyDown}
              onClick={() => {
                setQuery({ ...query, tab_active: paneKey });
                e?.onRowClick?.();
              }}>
              {e?.label}
            </button>
          );

          return (
            <li key={e?.key ?? e?.label} role='presentation' className={isActive ? 'bw_active' : ''}>
              {e?.permission ? (
                <CheckAccess permission={e?.permission}>{tabButton}</CheckAccess>
              ) : (
                tabButton
              )}
            </li>
          );
        })}
      </ul>

      {parsePanel.map((e) => {
        const { component: Component, key, ...props } = e;
        const paneKey = normalizePanelKey(key);
        const safePaneKey = paneKey.replace(/[^a-z0-9_-]/g, '-');
        const isActive = paneKey === normalizePanelKey(tab_active);
        if (!renderInactivePanes && !isActive) {
          return null;
        }
        return (
          <ComponentRender
            key={key}
            id={`${tabListId}-panel-${safePaneKey}`}
            role='tabpanel'
            aria-labelledby={`${tabListId}-tab-${safePaneKey}`}
            tabIndex={isActive ? 0 : -1}
            hidden={!isActive}>
            <Component {...props} />
          </ComponentRender>
        );
      })}
      {jsx_submit}
    </React.Fragment>
  );

  return (
    <React.Fragment>
      {hasSubmit ? <form onSubmit={methods.handleSubmit(onSubmit, handleInvalidSubmit)}>{jsx_main}</form> : jsx_main}
    </React.Fragment>
  );
};

export default Panel;
